"use client"

import React, { useState } from "react"
import { useEffect, useMemo } from "react"
import { Link, useNavigate, useSearchParams } from "react-router-dom"
import { ArrowLeft, Clock, Award, TrendingUp, Download } from "lucide-react"
import html2canvas from "html2canvas"
import jsPDF from "jspdf"
import { useAppSelector } from "../redux/hooks"
import { InterviewScore } from "../types/interview.type"
import { SessionStorage } from "../types/general.type"
import { useScreen } from "../contexts/screen.provider"
import { GoogleInFeedAd } from "../components/ads/GoogleInFeedAd"

const InterviewScoresPage: React.FC = () => {

  const navigate = useNavigate();
  //  get user
  const user = useAppSelector((state) => state.auth.user)
  const [interviewScoreReports, setInterviewScoreReports] = useState<InterviewScore[]>([])
  const scoreReport = JSON.parse(sessionStorage.getItem(SessionStorage.SCORE_REPORT) || "[]") as InterviewScore[];
  const overallReport = sessionStorage.getItem(SessionStorage.OVERALL_REPORT) || "N/A";
  const [searchParams] = useSearchParams();
  const jobTitle = searchParams.get('jobTitle') || '';
  const seniorityLevel = searchParams.get('seniority') || '';

  function toNumeric(score: string): number 
  {
    try
    {
      const cleaned = score?.replace('%', '').trim();
      const num = parseInt(cleaned, 10);
      return isNaN(num) ? 0 : num;
    }
    catch
    {
      return 0;
    }
  }

  useEffect(() => {

    if (scoreReport.length) {
      setInterviewScoreReports(scoreReport)
    }
  }, [scoreReport])


  // Convert scores to numbers and calculate stats
  const stats = useMemo(() => {
    if (!interviewScoreReports?.length) return { average: 0, highest: 0, lowest: 0 }

    const numericScores = interviewScoreReports.map((item) => Number.parseInt(item.score, 10))
    return {
      average: Math.round(numericScores.reduce((a, b) => a + b, 0) / numericScores.length),
      highest: Math.max(...numericScores),
      lowest: Math.min(...numericScores),
    }
  }, [interviewScoreReports])

  // const currentInterview = useMemo(() => {
  //   return interviews.find((position) => position.interviews.some(interview => interview.interviewId === Number.parseInt(interviewId || "0", 10)))
  // }, [interviews, interviewId])

  const downloadPDF = () => {
    function getTimestamp() {
      const now = new Date();
      const pad = (n: number) => String(n).padStart(2, '0');
      const date = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
      const time = `${pad(now.getHours())}-${pad(now.getMinutes())}`;
      return `${date}_${time}`;
    }

    const input = document.getElementById("pdf-content") // the component's DOM element
    if (input) {
      const ads = input.querySelectorAll(".google-ad");
      ads.forEach(ad => (ad as HTMLElement).style.display = "none");
      const timestamp = getTimestamp();
      const pdfFileName = `${user?.firstName || user?.primaryEmail}_Interview_Report_${timestamp}.pdf`;
      html2canvas(input)
        .then((canvas) => {
          const imgData = canvas.toDataURL("image/png")
          const pdf = new jsPDF({
            orientation: "portrait",
            unit: "px",
            format: [canvas.width, canvas.height],
          })
          pdf.addImage(imgData, "PNG", 0, 0, canvas.width, canvas.height, undefined, "FAST")
          pdf.save(pdfFileName);
        })
      ads.forEach(ad => (ad as HTMLElement).style.display = "");

    } else {
    }
  }


  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    })
  }

  return (

    <div className="min-h-screen   overflow-auto bg-gray-50 dark:bg-[#242424] text-gray-900 dark:text-gray-100">
      {/* Header */}
      <header className="bg-white dark:bg-[#272727] shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => {
                navigate('/interviews?pageIndex=0')
              }}
              className="inline-flex items-center text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors duration-200"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Interviews
            </button>

            {!!interviewScoreReports.length && stats && (
              <button
                className="relative inline-flex gap-2 rounded-md bg-accent-500 px-4 py-2 items-center text-sm font-medium text-white  hover:text-slate-50 transition-colors duration-200"
                onClick={downloadPDF}
              >
                <Download className=" relative h-5 w-5 " />
                <span className="hidden sm:inline">Download Report</span>
              </button>
            )}
          </div>
        </div>
      </header>


      <main id="pdf-content" className="max-w-7xl mx-auto px-4 bg-gray-50 dark:bg-[#1a1a1a] sm:px-6 lg:px-8 py-8 ">
        {/* Interview Overview */}
        <div className="mb-4  ">
          <div className="mb-4 flex gap-4 p-2  bg-white dark:bg-[#272727] rounded-xl shadow-sm items-center">
            <img src="/logo/green-logo.png" className="w-14 h-14 object-cover" alt="Logo" />
            <div className="flex  flex-grow items-start justify-center md:justify-between flex-col md:flex-row md:items-center">

              <div>
                <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-2">
                  <span className="text-teal-500">{user?.firstName}</span>, Interview Assessment
                </h1>
                <p className="text-gray-600 dark:text-gray-400 mb-2">
                  Detailed performance analysis and feedback for this interview session
                </p>
                <div className="flex text-sm sm:text-base items-center mb-1">
                  {/* <Calendar className="h-4 w-4 mr-1.5 text-gray-500 dark:text-gray-400" /> */}
                  <span>
                    {interviewScoreReports?.length
                      ? formatDate(interviewScoreReports[0].createdDate || new Date().toISOString())
                      : "No date available"}
                  </span>
                </div>
              </div>
              <div className="flex justify-center pr-6 flex-col items-start md:items-end flex-grow">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-500 dark:text-gray-400">Job Title:</span>
                  <span className="font-medium text-gray-900 dark:text-white">
                    <span className="text-slate-800 dark:text-slate-200 italic"> {jobTitle || 'No job title'} </span>
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-500 dark:text-gray-400">Seniority:</span>
                  <span className="font-medium text-gray-900 dark:text-white">
                    <span className="text-slate-800 dark:text-slate-200 italic"> {seniorityLevel || 'No seniority level'} </span>
                  </span>
                </div>
              </div>
            </div>


          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 bg-transparent dark:bg-[#272727]  rounded-xl md:grid-cols-3 gap-6 mb-8">
            <StatCard
              icon={<Award className="h-6 w-6 text-teal-500" />}
              title="Average Score"
              value={`${stats.average}%`}
              description="Overall performance"
              color="bg-gradient-to-br from-teal-50 to-teal-100 dark:from-teal-900/20 dark:to-teal-800/20"
              textColor="text-teal-700 dark:text-teal-300"
            />
            <StatCard
              icon={<TrendingUp className="h-6 w-6 text-blue-500" />}
              title="Highest Score"
              value={`${stats.highest}%`}
              description="Best performing area"
              color="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20"
              textColor="text-blue-700 dark:text-blue-300"
            />
            <StatCard
              icon={<Clock className="h-6 w-6 text-amber-500" />}
              title="Questions"
              value={interviewScoreReports?.length}
              description="Total questions answered"
              color="bg-gradient-to-br from-amber-50 to-amber-100 dark:from-amber-900/20 dark:to-amber-800/20"
              textColor="text-amber-700 dark:text-amber-300"
            />
          </div>
        </div>

        {/* Scores Detail */}
        {!interviewScoreReports?.length ? (
          <>
            <GoogleInFeedAd />
            <div className="bg-white dark:bg-[#272727] rounded-xl shadow-sm p-8 text-center">
              <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  className="w-8 h-8 text-gray-500 dark:text-gray-400"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                </svg>
              </div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">No Scores Available</h2>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                There are no assessment scores available for this interview session.
              </p>
              <Link
                to="/interviews"
                className="inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-teal-600 hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 transition-colors duration-200"
              >
                Return to Interviews
              </Link>
            </div></>
        ) : (
          <div className="space-y-6">
            {interviewScoreReports?.map((item, index) => {
              // Convert score to number
              const scoreNum = Number.parseInt(item.score, 10)
              return (
                <>
                  <ScoreCard
                    key={index}
                    question={item.question}
                    answer={item.userAnswer}
                    score={scoreNum}
                    strength={item.strength}
                    improvementArea={item.improvementArea}
                    index={index + 1}
                  />
                  {((index + 1) % 5 == 0 || index == 0) && (
                    <GoogleInFeedAd />
                  )}
                </>
              )
            })}
          </div>
        )}
        {/* Performance Summary Box */}
        <div className="mt-10 bg-white dark:bg-[#272727] rounded-xl shadow-sm ">
          <div className="px-6 py-5">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Performance Summary</h2>
            <div className="p-4 rounded-lg bg-gradient-to-r from-teal-50 to-blue-50 dark:from-teal-900/20 dark:to-blue-900/20">
            <div className="flex flex-col md:flex-row gap-4 xs:w-full sm:w-full md:w-full lg:w-5/6 xl:w-3/4">
                <div className="flex-1">
                    <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Overall Assessment</h3>
                    <div className="mb-3 text-gray-600 dark:text-gray-400 text-sm">
                      {overallReport}
                    </div>
                </div>
              </div>
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1">
                  {/* Average Score Status Bar */}
                  <div className="mb-3">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-medium text-gray-700 dark:text-gray-300">Average Score</span>
                      <span className="text-xs font-medium text-gray-700 dark:text-gray-300">{stats.average}%</span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5">
                      <div
                        className={`h-2.5 rounded-full ${stats.average >= 90
                          ? "bg-green-500"
                          : stats.average >= 70
                            ? "bg-blue-500"
                            : stats.average >= 50
                              ? "bg-amber-500"
                              : "bg-red-500"
                          }`}
                        style={{ width: `${stats.average}%` }}
                      ></div>
                    </div>
                  </div>

                  <p className="text-gray-600 dark:text-gray-400 text-sm">
                    {stats.average >= 90
                      ? "Excellent performance across all areas. Ready for advanced challenges."
                      : stats.average >= 75
                        ? "Strong performance with some areas of excellence. Good candidate with potential."
                        : stats.average >= 60
                          ? "Satisfactory performance with clear strengths and areas for improvement."
                          : "Needs additional preparation and practice in key areas."}
                  </p>
                </div>
                <div className="flex-1">
                  <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Key Strengths</h3>
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-2 h-2 rounded-full bg-green-500"></div>
                    <p className="text-gray-600 dark:text-gray-400 text-sm">
                    {interviewScoreReports.length > 0
                      ? interviewScoreReports.reduce((prev, current) =>
                          toNumeric(prev.score) > toNumeric(current.score) ? prev : current
                        ).strength
                      : "No data available"}
                    </p>
                  </div>
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Recommendation</h3>
                <p className="text-gray-600 dark:text-gray-400 text-sm">
                  {stats.average >= 80
                    ? "Consider advancing to the next interview stage. Candidate shows strong potential."
                    : stats.average >= 65
                      ? "Recommend additional technical discussion in specific areas before proceeding."
                      : "Suggest additional preparation and a follow-up interview to reassess skills."}
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}

// Helper component for stats cards
interface StatCardProps {
  icon: React.ReactNode
  title: string
  value: number | string
  description: string
  color: string
  textColor: string
}

const StatCard: React.FC<StatCardProps> = ({ icon, title, value, description, color, textColor }) => (
  <div className={`rounded-xl shadow-sm overflow-hidden ${color}`}>
    <div className="px-6 py-5">
      <div className="flex items-center">
        <div className="flex-shrink-0">{icon}</div>
        <div className="ml-5 w-0 flex-1">
          <dl>
            <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">{title}</dt>
            <dd>
              <div className={`text-3xl font-semibold ${textColor}`}>{value}</div>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{description}</p>
            </dd>
          </dl>
        </div>
      </div>
    </div>
  </div>
)

// Helper component for score cards
interface ScoreCardProps {
  question: string
  answer: string
  score: number
  strength: string
  improvementArea: string
  index: number
}

const ScoreCard: React.FC<ScoreCardProps> = ({ question, answer, score, strength, improvementArea, index }) => {
  // Determine color based on score
  let scoreColor = "bg-gray-500"
  let scoreTextColor = "text-gray-700 dark:text-gray-300"
  let scoreBgColor = "bg-gray-100 dark:bg-gray-700"

  if (score >= 90) {
    scoreColor = "bg-green-500"
    scoreTextColor = "text-green-700 dark:text-green-300"
    scoreBgColor = "bg-green-50 dark:bg-green-900/20"
  } else if (score >= 70) {
    scoreColor = "bg-blue-500"
    scoreTextColor = "text-blue-700 dark:text-blue-300"
    scoreBgColor = "bg-blue-50 dark:bg-blue-900/20"
  } else if (score >= 50) {
    scoreColor = "bg-amber-500"
    scoreTextColor = "text-amber-700 dark:text-amber-300"
    scoreBgColor = "bg-amber-50 dark:bg-amber-900/20"
  } else {
    scoreColor = "bg-red-500"
    scoreTextColor = "text-red-700 dark:text-red-300"
    scoreBgColor = "bg-red-50 dark:bg-red-900/20"
  }

  return (
    <div className="bg-white dark:bg-[#272727] rounded-xl shadow-sm overflow-hidden">
      <div className="px-6 py-5">
        <div className="flex items-start">
          <div className="flex-shrink-0 mr-4">
            <div className="w-10 h-10 rounded-full bg-teal-100 dark:bg-teal-900/30 flex items-center justify-center text-teal-600 dark:text-teal-400 font-semibold">
              {index}
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">{question}</h3>

            <div className="mb-4">
              <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Your Answer:</h4>
              <p className="text-gray-700 dark:text-gray-300 text-sm">{answer}</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              {/* Score */}
              <div className={`rounded-lg p-3 ${scoreBgColor}`}>
                <h4 className={`text-xs font-medium uppercase ${scoreTextColor} mb-2`}>Score</h4>
                <div className="flex items-center">
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5 mr-2">
                    <div className={`h-2.5 rounded-full ${scoreColor}`} style={{ width: `${score}%` }}></div>
                  </div>
                  <span className={`text-sm font-medium ${scoreTextColor}`}>{score}%</span>
                </div>
              </div>

              {/* Strength */}
              <div className="rounded-lg p-3 bg-blue-50 dark:bg-blue-900/20">
                <h4 className="text-xs font-medium uppercase text-blue-700 dark:text-blue-300 mb-2">Strength</h4>
                <p className="text-gray-700 dark:text-gray-300 text-sm">{strength}</p>
              </div>

              {/* Improvement */}
              <div className="rounded-lg p-3 bg-amber-50 dark:bg-amber-900/20">
                <h4 className="text-xs font-medium uppercase text-amber-700 dark:text-amber-300 mb-2">
                  Improvement Area
                </h4>
                <p className="text-gray-700 dark:text-gray-300 text-sm">{improvementArea}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default InterviewScoresPage
